import { useEffect, useState } from "react";
import PromineoVerificationCodeInput from "components/common/PromineoVerificationCodeInput";
import Button from "components/common/controls/Button";
import { SignInSignUpOption } from "shared/enums/SignInSignUpOptionEnum";
import Link from "components/common/LinkButton";
import PreviousRedIconSvg from "static/images/previous-icon-red.svg";
import EditorDescriptor from "fetaures/authentication/EditorDescriptor";
import VerificationCodeCountDownTimer from "./VerificationCodeCountDownTimer";
import { 
  requestForEmailVerificationCodeAsync, 
  requestForPhoneVerificationCodeAsync 
} from "apis/VerificationCodeApi";
import EmailVerificationCodeRequest from "interfaces/request/EmailVerificationCodeRequest";
import PhoneVerificationCodeRequest from "interfaces/request/PhoneVerificationCodeRequest";
import VerificationResponse from "interfaces/response/VerificationResponse";
import Loader from "components/common/controls/Loader";

export interface VerificationCodeProviderProps {
  expireTime: string;
  token: string;
  codeSendingAddress: string;
  authenticationType: SignInSignUpOption;
  onVerificationCodeInputComplete: (code: string) => void;
  backLinkText?: string;
  onBackLinkClick?: () => void;
}

export default function VerificationCodeProvider(props: VerificationCodeProviderProps) {
  const {
    expireTime,
    token,
    codeSendingAddress,
    authenticationType,
    backLinkText,
    onBackLinkClick,
    onVerificationCodeInputComplete,
  } = props;

  const [verificationCode, setVerificationCode] = useState("");
  const [expiryTime, setExpiryTime] = useState<string>(expireTime);
  const [isLoading, setIsLoading] = useState(false);
  const numberOfFields = 5;

  useEffect(() => {
    setExpiryTime(expireTime);
  }, [expireTime]);

  const BackLink = ({
    text,
    onClick,
  }: {
    text: string;
    onClick: () => void;
  }) => {
    return (
      <div onClick={onClick} className="cursor-pointer inline-block">
        <img src={PreviousRedIconSvg} className="inline-block" alt="back" />
        <Link className="font-bold text-primaryRed ml-1"> {text}</Link>
      </div>
    );
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    const resendVerificationCodeForEmailRequest: EmailVerificationCodeRequest = {
      token: token,
    };

    const resendVerificationCodeForPhoneRequest: PhoneVerificationCodeRequest = {
      token: token,
    };

    if (authenticationType === SignInSignUpOption.Email) {
      resendVerificationCodeForEmailRequest.email = codeSendingAddress;

      await requestForEmailVerificationCodeAsync(resendVerificationCodeForEmailRequest)
      .then((response: VerificationResponse) => {
        setExpiryTime(response.expiryTime);
      })
      .finally(() => {
        setIsLoading(false);
      });

    } else if (authenticationType === SignInSignUpOption.Phone) {
      resendVerificationCodeForPhoneRequest.phone = codeSendingAddress;
      
      await requestForPhoneVerificationCodeAsync(resendVerificationCodeForPhoneRequest)
      .then((response: VerificationResponse) => {
        setExpiryTime(response.expiryTime);
      })
      .finally(() => {
        setIsLoading(false);
      });
    }
  };

  const handleVerificationCodeChange = (value: string) => {
    setVerificationCode(value);
  };

  const handleVerificationCodeInputComplete = () => {
    onVerificationCodeInputComplete(verificationCode);
  };

  return (
    <>
      <EditorDescriptor
        title={`${
          authenticationType === SignInSignUpOption.Email
            ? "Email"
            : "Phone"
        } Verification`}
        description={`Please enter your verification
    code sent to: ${codeSendingAddress}`}
      >
        <>
          <div className="mb-2">
            <div className="text-sm font-medium inline">Verification code</div>
          </div>
          <PromineoVerificationCodeInput
            fields={numberOfFields}
            type={"number"}
            onChange={handleVerificationCodeChange}
            key={expiryTime}
          />
          <div className="mt-4">
            <div className="flex justify-center sm:inline">
              <Button
                text="CONTINUE"
                className="w-auto sm:w-full"
                onClick={handleVerificationCodeInputComplete}
                disabled={numberOfFields !== verificationCode.length}
              />
            </div>
          </div>
          <div className="my-6">
            <VerificationCodeCountDownTimer expireAt={expiryTime} />
          </div>
          <div className="font-medium text-sm">
            <div>
              Not receiving a validation code on that{" "}
              {authenticationType === SignInSignUpOption.Phone
                ? "number"
                : "email"}
              ?
            </div>
            <div className="mt-2">
              Please click{" "}
              <Link className="text-primaryRed" onClick={handleResendCode}>
                here
              </Link>{" "}
              to resend the code.
            </div>
          </div>
          {backLinkText && onBackLinkClick && (
            <div className="mt-4">
              <BackLink text={backLinkText} onClick={onBackLinkClick} />
            </div>
          )}
        </>
      </EditorDescriptor>
      <Loader isVisible={isLoading} />
    </>
  );
}
